import { useState, useEffect } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Users, Video, DollarSign, Shield, MessageSquare } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useAdminLanguage();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const StatCard = ({ title, value, icon: Icon, color = "indigo" }) => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-2 bg-${color}-500/10 rounded-lg`}>
            <Icon className={`w-5 h-5 text-${color}-400`} />
          </div>
        </div>
        <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{title}</p>
        <h3 className="text-2xl font-bold text-white mt-1">{value}</h3>
      </CardContent>
    </Card>
  );

  const chartTooltipStyle = { backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff', fontSize: '12px' };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-1">{t("dashboard")}</h1>
        <p className="text-zinc-500 text-sm">{t("overview")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard title={t("total_companies")} value={stats?.total_companies || 0} icon={Users} />
        <StatCard title={t("completed_interviews")} value={stats?.completed_interviews || 0} icon={Video} />
        <StatCard title={t("revenue")} value={`$${stats?.revenue || 0}`} icon={DollarSign} />
        <StatCard title={t("total_employees")} value={stats?.total_employees || 0} icon={Shield} />
        <StatCard title={t("pending_testimonials")} value={stats?.pending_testimonials || 0} icon={MessageSquare} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3">{t("interviews_chart")}</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats?.interviews_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                <YAxis tick={{ fill: '#71717a', fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3">{t("revenue_chart")}</h3>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={stats?.revenue_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [`$${v}`, 'Revenue']} />
                <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3">{t("users_chart")}</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats?.users_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                <YAxis tick={{ fill: '#71717a', fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Signups + System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-zinc-300 mb-4">{t("recent_signups")}</h3>
            <div className="space-y-3">
              {stats?.recent_users?.map(user => (
                <div key={user.email} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-500/20 rounded-full flex items-center justify-center text-indigo-400 font-bold text-sm">
                      {user.name?.[0] || "?"}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500">{user.created_at ? new Date(user.created_at).toLocaleDateString() : ""}</p>
                </div>
              ))}
              {(!stats?.recent_users || stats.recent_users.length === 0) && (
                <p className="text-zinc-600 text-sm text-center py-4">{t("no_data")}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-zinc-300 mb-4">{t("system_health")}</h3>
            <div className="space-y-5">
              {[
                { name: "Gemini API", status: t("operational"), pct: 100 },
                { name: "GridFS Storage", status: t("operational"), pct: 100 },
                { name: "Resend Emails", status: "99.9%", pct: 99 },
                { name: "Stripe Payments", status: t("operational"), pct: 100 },
              ].map(s => (
                <div key={s.name}>
                  <div className="flex justify-between mb-1.5 text-xs">
                    <span className="text-zinc-400">{s.name}</span>
                    <span className="text-emerald-400">{s.status}</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
